/*
  WORK'S DATE WORDS, in one pure module (wave 23).

  Today's rank was computed here, over work items alone, and then on the server
  too, the two pinned together by `tools/parity/work-rank.json`. Since ADR-0050
  Today holds everything that needs a person (tasks and records as well as work
  items), and the SERVER ranks it: the Today screen and the chat read one list
  from `GET /today`, so they cannot disagree. The browser's copy of the rank is
  retired.

  What stays is what every row still needs: the person's day (`dayOf`), a due
  date's tone, the default order, the outcome walk and the drift and quiet
  signals a row shows. The parity check still runs this module in Node, for the
  day an instant falls on in each zone, which is why it has NO runtime imports.
*/
import type { PlanNode, WatchedOutcome, WorkItemView } from '../api/types'

/** What the rank reads from a row. Structural, so a full `WorkEntry` passes too. */
export type RankedRow = {
  title: string
  /** YYYY-MM-DD, or null for a row with no date. */
  due: string | null
  closed: boolean
  drifting: boolean
  staleDays: number | null
}

/*
  THE PERSON'S DAY (RAID #482, ruled by Erik 2026-09-21 and 22). "Today" is the
  date where the person is, in the zone they set in Settings, and Eastern until
  they set one. Until wave 23 this module took the UTC date (`toISOString`), so
  from 7pm Central an item due today read overdue. The server's `PersonTime`
  counts the same day, and the parity fixture's `days` holds the two together.

  Here, not in a helper module, for the reason this file has no runtime
  imports: Node runs it directly in the parity check.
*/

/** Eastern: the day a person's dates count in until they choose a zone. */
export const DEFAULT_ZONE = 'America/New_York'

const dayFormats = new Map<string, Intl.DateTimeFormat>()

/** The date an instant falls on in a zone, as YYYY-MM-DD. */
export function dayOf(instant: Date, zone: string): string {
  let format = dayFormats.get(zone)
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' })
    dayFormats.set(zone, format)
  }
  const part = (type: string) => format.formatToParts(instant).find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** Overdue is red, due today is yellow, a closed row is quiet (011 rule 1). */
export function dueTone(
  entry: Pick<RankedRow, 'closed' | 'due'>,
  zone: string,
  today = new Date(),
): 'overdue' | 'today' | 'quiet' | null {
  if (entry.closed) return 'quiet'
  if (!entry.due) return null
  const stamp = dayOf(today, zone)
  if (entry.due < stamp) return 'overdue'
  if (entry.due === stamp) return 'today'
  return null
}

/**
 * Default sort: due ascending (011's reference implementation, "both
 * default-sort by due ascending"). Rows with no due date sort last rather than
 * first, because a dateless row is not the most urgent thing on the page.
 */
export function byDueAscending(a: Pick<RankedRow, 'due' | 'title'>, b: Pick<RankedRow, 'due' | 'title'>): number {
  if (a.due === b.due) return a.title.localeCompare(b.title)
  if (!a.due) return 1
  if (!b.due) return -1
  return a.due < b.due ? -1 : 1
}

/**
 * The outcome a work item traces to, walking the ratified edge grammar
 * backwards: a work item is about a task, a deliverable is `produced_by` that
 * task, and that deliverable `enables` an outcome.
 *
 * Null for a standalone item (G-27), for a subject outside the plan, and for a
 * task nothing produces. All three are ordinary rather than errors, and all
 * three mean the same thing here: nothing to drift against.
 */
export function outcomeBehind(
  subjectRef: string | null | undefined,
  plan: readonly Pick<PlanNode, 'nodeType' | 'edges'>[],
): string | null {
  const separator = subjectRef?.indexOf(':') ?? -1
  if (separator < 0) return null
  const subjectId = subjectRef!.slice(separator + 1)

  const deliverable = plan.find(
    (n) =>
      n.nodeType === 'deliverable' &&
      (n.edges ?? []).some((e) => e.edgeType === 'produced_by' && e.toId === subjectId),
  )
  return (deliverable?.edges ?? []).find((e) => e.edgeType === 'enables')?.toId ?? null
}

/** Whole days between a timestamp and now, or null when there is none. */
export function daysSince(at: string | null | undefined, now = new Date()): number | null {
  if (!at) return null
  const then = new Date(at).getTime()
  if (Number.isNaN(then)) return null
  return Math.floor((now.getTime() - then) / 86_400_000)
}

/**
 * The outcomes surveillance reads as moving: at risk or off track. Drift is the
 * outcome's COMPUTED state, never the declared one. A person who says "on
 * track" over work that is slipping should not thereby take their own work off
 * the list; the contradiction is the nudge tray's business, and it is
 * addressed to them rather than hidden from them.
 */
export function driftingOutcomes(watch: readonly Pick<WatchedOutcome, 'outcomeId' | 'computed'>[]): Set<string> {
  return new Set(
    watch.filter((o) => o.computed === 'at-risk' || o.computed === 'off-track').map((o) => o.outcomeId),
  )
}

/** The two signals Today's rubric bands on, for one item. */
export function todaySignals(
  item: Pick<WorkItemView, 'subjectRef' | 'lastTouchedAt'>,
  plan: readonly Pick<PlanNode, 'nodeType' | 'edges'>[],
  drifting: ReadonlySet<string>,
  now = new Date(),
): { drifting: boolean; staleDays: number | null } {
  const outcomeId = outcomeBehind(item.subjectRef, plan)
  return {
    drifting: outcomeId !== null && drifting.has(outcomeId),
    staleDays: daysSince(item.lastTouchedAt, now),
  }
}
